// Questionario pre-analisi cute — INTERVENTO 3 / passo 3a.
// Solo raccolta dati. Non è ancora collegato al motore di raccomandazione.
// Tutte le domande sono opzionali: l'analisi parte anche senza risposte.
//
// Convenzione: il componente è controllato. Il parent gestisce lo state
// (`value`) e riceve gli aggiornamenti via `onChange(nextValue)`.
// `value` è un oggetto piatto del tipo:
//   { sensazione_cute: "grassa", prurito: "ogni_tanto", ... }
// I campi non risposti restano assenti dall'oggetto (mai null/undefined
// in chiaro, così model_dump(exclude_none=True) lato backend resta pulito).

import React, { useState } from "react";
import { ChevronDown, ChevronRight, ClipboardList } from "lucide-react";

const BLOCKS = [
  {
    title: "Sensazioni cute",
    questions: [
      {
        id: "sensazione_cute",
        label: "Come percepisci la tua cute?",
        options: [
          { value: "normale", label: "Normale" },
          { value: "grassa",  label: "Grassa" },
          { value: "secca",   label: "Secca" },
          { value: "non_so",  label: "Non saprei" },
        ],
      },
      {
        id: "prurito",
        label: "Avverti prurito?",
        options: [
          { value: "mai",        label: "Mai" },
          { value: "ogni_tanto", label: "Ogni tanto" },
          { value: "spesso",     label: "Spesso" },
        ],
      },
      {
        id: "desquamazione",
        label: "Hai notato desquamazione o forfora?",
        options: [
          { value: "no",       label: "No" },
          { value: "leggera",  label: "Leggera" },
          { value: "evidente", label: "Evidente" },
        ],
      },
      {
        id: "frequenza_lavaggio",
        label: "Dopo quanto tempo senti il bisogno di lavare i capelli?",
        options: [
          { value: "1g",       label: "1 giorno" },
          { value: "2g",       label: "2 giorni" },
          { value: "3g_o_piu", label: "3 giorni o più" },
        ],
      },
      {
        id: "fastidio_cute",
        label: "La cute ti dà fastidio?",
        options: [
          { value: "no",        label: "No" },
          { value: "tensione",  label: "Sensazione di tensione" },
          { value: "bruciore",  label: "Sensazione di bruciore" },
          { value: "tatto",     label: "Sensibilità al tocco" },
        ],
      },
    ],
  },
  {
    title: "Caduta",
    questions: [
      {
        id: "caduta",
        label: "Hai notato una maggiore caduta negli ultimi mesi?",
        options: [
          { value: "no",         label: "No" },
          { value: "leggera",    label: "Leggera" },
          { value: "moderata",   label: "Moderata" },
          { value: "importante", label: "Importante" },
        ],
      },
      {
        id: "caduta_durata",
        label: "Da quanto tempo?",
        options: [
          { value: "<1m",   label: "Meno di 1 mese" },
          { value: "1-3m",  label: "1-3 mesi" },
          { value: ">3m",   label: "Più di 3 mesi" },
        ],
      },
    ],
  },
  {
    title: "Storico",
    questions: [
      {
        id: "trattamenti_chimici",
        label: "Hai effettuato trattamenti chimici recentemente?",
        options: [
          { value: "no",          label: "No" },
          { value: "colore",      label: "Colore" },
          { value: "schiariture", label: "Schiariture" },
          { value: "permanente",  label: "Permanente" },
          { value: "piu",         label: "Più trattamenti" },
        ],
      },
      {
        id: "prodotti_cute_in_uso",
        label: "Stai utilizzando prodotti specifici per la cute?",
        options: [
          { value: "no", label: "No" },
          { value: "si", label: "Sì" },
        ],
      },
    ],
  },
  {
    title: "Contesto",
    // Domanda di contesto secondario. Vincolo brief 3a-update:
    // SOLO modulatore tono, MAI input diagnostico, MAI peso sui tag.
    questions: [
      {
        id: "fascia_eta",
        label: "Fascia d'età (dato di contesto, opzionale)",
        options: [
          { value: "18-24",        label: "18-24" },
          { value: "25-34",        label: "25-34" },
          { value: "35-44",        label: "35-44" },
          { value: "45-54",        label: "45-54" },
          { value: "55-64",        label: "55-64" },
          { value: "65+",          label: "65+" },
          { value: "no_risposta",  label: "Preferisco non rispondere" },
        ],
      },
    ],
  },
];

export default function CuteQuestionnaire({ value, onChange }) {
  const [open, setOpen] = useState(true);
  const v = value || {};

  const setAnswer = (qid, ans) => {
    const next = { ...v };
    if (next[qid] === ans) {
      // Click sulla stessa risposta → toggle off (la cliente può
      // ripensarci: la domanda torna "senza risposta").
      delete next[qid];
    } else {
      next[qid] = ans;
    }
    onChange?.(next);
  };

  const answered = Object.keys(v).length;
  const total = BLOCKS.reduce((acc, b) => acc + b.questions.length, 0);

  return (
    <section
      className="rounded-2xl border border-[#E5E5E0] bg-white mb-8 animate-fade-up"
      data-testid="cute-questionnaire"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full p-5 lg:p-6 flex items-center justify-between gap-4 text-left hover:bg-[#FAFAFA] transition-colors rounded-2xl"
        data-testid="cute-questionnaire-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#EAE5DF] flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-[#C5A059]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#C5A059] font-medium">
              Pre-analisi (opzionale)
            </p>
            <h3 className="font-serif-display text-xl text-[#1A1A1A] mt-0.5">
              Questionario cute
            </h3>
            <p className="text-sm text-[#737373] mt-1">
              Domande veloci per arricchire il contesto della consulenza.{" "}
              {answered > 0
                ? `${answered}/${total} compilate.`
                : "Puoi anche saltarle e procedere all'analisi."}
            </p>
          </div>
        </div>
        {open ? (
          <ChevronDown className="w-5 h-5 text-[#737373] shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-[#737373] shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-[#E5E5E0] p-5 lg:p-6 space-y-8">
          {BLOCKS.map((block) => (
            <div key={block.title}>
              <p className="text-[11px] uppercase tracking-wider text-[#737373] font-semibold mb-4">
                {block.title}
              </p>
              <div className="space-y-5">
                {block.questions.map((q) => (
                  <div key={q.id} data-testid={`cute-q-${q.id}`}>
                    <p className="text-sm text-[#1A1A1A] mb-2.5">{q.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map((opt) => {
                        const active = v[q.id] === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAnswer(q.id, opt.value)}
                            className={[
                              "h-10 px-4 rounded-full text-sm font-medium transition-colors border",
                              active
                                ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                                : "bg-white text-[#1A1A1A] border-[#E5E5E0] hover:border-[#C5A059] hover:text-[#C5A059]",
                            ].join(" ")}
                            data-testid={`cute-q-${q.id}-opt-${opt.value}`}
                            aria-pressed={active}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
